import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    AlertTriangle,
    Building2,
    Camera,
    CheckCircle2,
    Clock,
    Download,
    ExternalLink,
    FileSpreadsheet,
    FileText,
    MapPin,
    ScanLine,
    Send,
    Users,
    X
} from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { logUsageEvent } from '../../../src/lib/logger';
import { Profile } from '../../../types';
import LoadingSpinner from '../../../src/components/LoadingSpinner';
import { useToast } from '../../../src/contexts/ToastContext';
import {
    DISTRIBUIDORA_PADRAO,
    MedidorExportRow,
    encontrarDuplicados,
    exportMedidoresCsv,
    exportMedidoresEquatorial,
    exportMedidoresExcel,
    exportMedidoresPdf,
} from '../../../src/lib/exportMedidores';

interface Captura {
    id: string;
    foto_url: string;
    status?: string | null;
    checkin_em?: string | null;
    checkout_em?: string | null;
    checkin_lat?: number | null;
    checkin_lng?: number | null;
    descricao_visita?: string | null;
    numero_medidor?: string | null;
    ocr_status?: string | null;
    ocr_candidatos?: Array<{ numero: string; score: number }> | null;
    medidor_exportado_em?: string | null;
    rotas_comerciais?: { id: string; nome?: string | null; data: string; vendedor_id: string } | null;
    negociacoes?: { id?: number | null; titulo?: string | null; cidade?: string | null; bairro?: string | null; cpf_cnpj?: string | null } | null;
}

/** Situação da leitura do medidor de uma captura (p/ filtro e KPIs). */
type StatusLeitura = 'confirmado' | 'aguardando' | 'ocr_falhou' | 'sem_numero';

const statusLeitura = (c: Captura): StatusLeitura => {
    if (c.numero_medidor) return 'confirmado';
    if (c.status === 'em_visita') return 'aguardando'; // foto tirada, conferência pendente
    if (c.ocr_status === 'falhou') return 'ocr_falhou';
    return 'sem_numero';
};

const STATUS_LEITURA_OPTS: Array<{ value: 'todos' | StatusLeitura; label: string }> = [
    { value: 'todos', label: 'Todas as leituras' },
    { value: 'confirmado', label: 'Nº confirmado' },
    { value: 'aguardando', label: 'Aguardando conferência' },
    { value: 'ocr_falhou', label: 'OCR falhou' },
    { value: 'sem_numero', label: 'Sem número' },
];

type Periodo = 'hoje' | '7d' | '14d' | '30d' | '90d' | 'custom';

const PERIODOS: Array<{ value: Periodo; label: string }> = [
    { value: 'hoje', label: 'Hoje' },
    { value: '7d', label: '7 dias' },
    { value: '14d', label: '14 dias' },
    { value: '30d', label: '30 dias' },
    { value: '90d', label: '90 dias' },
    { value: 'custom', label: 'Personalizado' },
];

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const rangeDoPeriodo = (p: Periodo, customIni: string, customFim: string): [string, string] => {
    const hoje = new Date();
    switch (p) {
        case 'hoje': return [iso(hoje), iso(hoje)];
        case '7d': { const d = new Date(hoje); d.setDate(d.getDate() - 6); return [iso(d), iso(hoje)]; }
        case '14d': { const d = new Date(hoje); d.setDate(d.getDate() - 13); return [iso(d), iso(hoje)]; }
        case '30d': { const d = new Date(hoje); d.setDate(d.getDate() - 29); return [iso(d), iso(hoje)]; }
        case '90d': { const d = new Date(hoje); d.setDate(d.getDate() - 89); return [iso(d), iso(hoje)]; }
        case 'custom': return [customIni || iso(hoje), customFim || iso(hoje)];
    }
};

interface CapturasPanelProps {
    profile: Profile | null;
    profiles: Profile[];
    vendedorSel: string; // '' = toda a equipe
}

/** Painel de Capturas (Motoboy M2): galeria de fotos de medidor com data, hora, local e operador. */
const CapturasPanel: React.FC<CapturasPanelProps> = ({ profile, profiles, vendedorSel }) => {
    const { addToast } = useToast();
    const [periodo, setPeriodo] = useState<Periodo>('30d');
    const [customIni, setCustomIni] = useState(iso(new Date()));
    const [customFim, setCustomFim] = useState(iso(new Date()));
    const [cidadeFiltro, setCidadeFiltro] = useState('todas');
    const [rotaFiltro, setRotaFiltro] = useState('todas');
    const [leituraFiltro, setLeituraFiltro] = useState<'todos' | StatusLeitura>('todos');
    const [capturas, setCapturas] = useState<Captura[]>([]);
    const [enderecos, setEnderecos] = useState<Record<number, string>>({}); // negociacao_id → endereço completo (empresas_prospectadas)
    const [loading, setLoading] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);
    // Modal de duplicatas antes da exportação Equatorial
    const [dupModal, setDupModal] = useState<{ rows: MedidorExportRow[]; grupos: Array<[string, MedidorExportRow[]]> } | null>(null);
    const [dupSelecionados, setDupSelecionados] = useState<Set<string>>(new Set());
    const [exportando, setExportando] = useState(false);

    const [ini, fim] = rangeDoPeriodo(periodo, customIni, customFim);

    useEffect(() => {
        const fetchCapturas = async () => {
            if (!profile) return;
            setLoading(true);
            try {
                let query = supabase
                    .from('rota_paradas')
                    .select('id, foto_url, status, checkin_em, checkout_em, checkin_lat, checkin_lng, descricao_visita, numero_medidor, ocr_status, ocr_candidatos, medidor_exportado_em, rotas_comerciais!inner(id, nome, data, vendedor_id), negociacoes(id, titulo, cidade, bairro, cpf_cnpj)')
                    .not('foto_url', 'is', null)
                    // Filtra pela data em que a captura aconteceu (check-in, fuso de Goiânia),
                    // não pela data planejada da rota — rota agendada p/ amanhã pode ser executada hoje.
                    .gte('checkin_em', `${ini}T00:00:00-03:00`)
                    .lte('checkin_em', `${fim}T23:59:59.999-03:00`)
                    .order('checkin_em', { ascending: false })
                    .limit(300);
                if (vendedorSel) query = query.eq('rotas_comerciais.vendedor_id', vendedorSel);
                const { data, error } = await query;
                if (error) throw error;
                const lista = (data || []) as unknown as Captura[];
                setCapturas(lista);

                // Endereço completo vem do pool de prospecção (negociacoes só tem cidade/bairro)
                const negIds = Array.from(new Set(lista.map(c => c.negociacoes?.id).filter(Boolean))) as number[];
                if (negIds.length > 0) {
                    const { data: emps } = await supabase
                        .from('empresas_prospectadas')
                        .select('negociacao_id, endereco')
                        .in('negociacao_id', negIds);
                    const map: Record<number, string> = {};
                    for (const e of emps || []) {
                        if (e.negociacao_id && e.endereco) map[e.negociacao_id] = e.endereco;
                    }
                    setEnderecos(map);
                } else {
                    setEnderecos({});
                }
            } catch (err) {
                console.error('Erro ao carregar capturas:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchCapturas();
    }, [profile?.id, vendedorSel, ini, fim, refreshKey]);

    const nome = (id?: string | null) => profiles.find(p => String(p.id) === String(id))?.full_name?.split(' ').slice(0, 2).join(' ') || '—';

    const rotasDisponiveis = useMemo(() => {
        const map = new Map<string, string>();
        for (const c of capturas) {
            if (c.rotas_comerciais) map.set(c.rotas_comerciais.id, c.rotas_comerciais.nome || `Rota de ${new Date(c.rotas_comerciais.data + 'T12:00:00').toLocaleDateString('pt-BR')}`);
        }
        return Array.from(map.entries());
    }, [capturas]);

    const filtradas = useMemo(
        () => capturas.filter(c =>
            (cidadeFiltro === 'todas' || (c.negociacoes?.cidade || '') === cidadeFiltro)
            && (rotaFiltro === 'todas' || c.rotas_comerciais?.id === rotaFiltro)
            && (leituraFiltro === 'todos' || statusLeitura(c) === leituraFiltro)
        ),
        [capturas, cidadeFiltro, rotaFiltro, leituraFiltro]
    );

    /** Linhas exportáveis (apenas capturas com nº de medidor confirmado). */
    const exportRows = useMemo((): MedidorExportRow[] =>
        filtradas.filter(c => c.numero_medidor).map(c => ({
            paradaId: c.id,
            empresa: c.negociacoes?.titulo || '—',
            cnpj: c.negociacoes?.cpf_cnpj || '',
            endereco: (c.negociacoes?.id && enderecos[c.negociacoes.id])
                || [c.negociacoes?.bairro, c.negociacoes?.cidade].filter(Boolean).join(', ')
                || '',
            cidade: c.negociacoes?.cidade || '',
            distribuidora: DISTRIBUIDORA_PADRAO,
            numeroMedidor: c.numero_medidor!,
            dataCaptura: c.checkin_em
                ? `${new Date(c.checkin_em).toLocaleDateString('pt-BR')} ${new Date(c.checkin_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
                : (c.rotas_comerciais ? new Date(c.rotas_comerciais.data + 'T12:00:00').toLocaleDateString('pt-BR') : ''),
            operador: nome(c.rotas_comerciais?.vendedor_id),
            latitude: c.checkin_lat ?? null,
            longitude: c.checkin_lng ?? null,
        })),
        [filtradas, enderecos, profiles]
    );

    const exportar = (formato: 'excel' | 'csv' | 'pdf') => {
        if (exportRows.length === 0) {
            addToast('Nenhuma captura com número de medidor confirmado nos filtros atuais.', 'info');
            return;
        }
        if (formato === 'excel') exportMedidoresExcel(exportRows);
        else if (formato === 'csv') exportMedidoresCsv(exportRows);
        else exportMedidoresPdf(exportRows);
        addToast(`${exportRows.length} medidor(es) exportado(s).`, 'success');
    };

    /** Exportação Equatorial: alerta de duplicidade antes de gerar a planilha. */
    const iniciarExportEquatorial = () => {
        if (exportRows.length === 0) {
            addToast('Nenhuma captura com número de medidor confirmado nos filtros atuais.', 'info');
            return;
        }
        const dups = encontrarDuplicados(exportRows);
        if (dups.size === 0) {
            concluirExportEquatorial(exportRows);
            return;
        }
        // Pré-seleciona só a captura mais recente de cada nº duplicado
        const manter = new Set(exportRows.map(r => r.paradaId));
        for (const [, grupo] of dups) grupo.slice(1).forEach(r => manter.delete(r.paradaId));
        setDupSelecionados(manter);
        setDupModal({ rows: exportRows, grupos: Array.from(dups.entries()) });
    };

    const concluirExportEquatorial = async (rows: MedidorExportRow[]) => {
        setExportando(true);
        try {
            exportMedidoresEquatorial(rows);
            await supabase.from('rota_paradas')
                .update({ medidor_exportado_em: new Date().toISOString() })
                .in('id', rows.map(r => r.paradaId));
            addToast(`Planilha Equatorial gerada com ${rows.length} medidor(es). ✅`, 'success');
            logUsageEvent({ userId: profile?.id, modulo: 'crm', funcionalidade: 'rotas', acao: 'exportar_medidores_equatorial', origem: 'acao' });
            setDupModal(null);
            setRefreshKey(k => k + 1);
        } catch (err) {
            console.error('Erro na exportação Equatorial:', err);
            addToast('Erro ao registrar a exportação.', 'error');
        } finally {
            setExportando(false);
        }
    };

    const stats = useMemo(() => {
        const operadores = new Set(capturas.map(c => c.rotas_comerciais?.vendedor_id).filter(Boolean));
        const porCidade: Record<string, number> = {};
        const porOperador: Record<string, number> = {};
        const porDia: Record<string, number> = {};
        for (const c of capturas) {
            const cid = c.negociacoes?.cidade?.trim();
            if (cid) porCidade[cid] = (porCidade[cid] || 0) + 1;
            const op = c.rotas_comerciais?.vendedor_id;
            if (op) porOperador[op] = (porOperador[op] || 0) + 1;
            const dia = c.checkin_em ? c.checkin_em.slice(0, 10) : c.rotas_comerciais?.data;
            if (dia) porDia[dia] = (porDia[dia] || 0) + 1;
        }
        const reconhecidosOcr = capturas.filter(c => c.ocr_status === 'concluido' && (c.ocr_candidatos?.length || 0) > 0).length;
        const ocrRodados = capturas.filter(c => c.ocr_status === 'concluido' || c.ocr_status === 'falhou').length;
        return {
            total: capturas.length,
            operadores: operadores.size,
            cidades: Object.keys(porCidade).length,
            porCidade: Object.entries(porCidade).sort((a, b) => b[1] - a[1]),
            porOperador: Object.entries(porOperador).sort((a, b) => b[1] - a[1]),
            porDia: Object.entries(porDia).sort((a, b) => b[0].localeCompare(a[0])).slice(0, 14),
            // Dashboard de medidores
            reconhecidosOcr,
            taxaOcr: ocrRodados > 0 ? (reconhecidosOcr / ocrRodados) * 100 : null,
            aguardando: capturas.filter(c => statusLeitura(c) === 'aguardando').length,
            confirmados: capturas.filter(c => !!c.numero_medidor).length,
            exportados: capturas.filter(c => !!c.medidor_exportado_em).length,
        };
    }, [capturas]);

    const maxDia = Math.max(1, ...stats.porDia.map(([, q]) => q));

    return (
        <div className="space-y-4">
            {/* Filtros */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-3 flex flex-wrap items-center gap-2">
                <Camera size={15} className="text-gray-400 ml-1" />
                <span className="text-xs font-bold text-gray-700 dark:text-gray-200 mr-1">
                    Capturas de Campo {vendedorSel ? `· ${nome(vendedorSel)}` : '· Toda a Equipe'}
                </span>
                <div className="flex items-center gap-1 flex-wrap ml-auto">
                    <select
                        value={leituraFiltro}
                        onChange={e => setLeituraFiltro(e.target.value as 'todos' | StatusLeitura)}
                        className="text-xs font-medium px-2.5 py-1.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-none focus:outline-none mr-1"
                    >
                        {STATUS_LEITURA_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    {rotasDisponiveis.length > 1 && (
                        <select
                            value={rotaFiltro}
                            onChange={e => setRotaFiltro(e.target.value)}
                            className="text-xs font-medium px-2.5 py-1.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-none focus:outline-none mr-1 max-w-[160px]"
                        >
                            <option value="todas">Todas as rotas</option>
                            {rotasDisponiveis.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                        </select>
                    )}
                    {stats.porCidade.length > 0 && (
                        <select
                            value={cidadeFiltro}
                            onChange={e => setCidadeFiltro(e.target.value)}
                            className="text-xs font-medium px-2.5 py-1.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-none focus:outline-none mr-1"
                        >
                            <option value="todas">Todas as cidades</option>
                            {stats.porCidade.map(([c]) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    )}
                    {PERIODOS.map(p => (
                        <button
                            key={p.value}
                            onClick={() => setPeriodo(p.value)}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-all ${periodo === p.value
                                ? 'bg-primary text-white shadow-sm'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                        >
                            {p.label}
                        </button>
                    ))}
                    {periodo === 'custom' && (
                        <span className="flex items-center gap-1.5 ml-1">
                            <input type="date" value={customIni} onChange={e => setCustomIni(e.target.value)}
                                className="text-xs px-2 py-1.5 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 focus:outline-none" />
                            <span className="text-xs text-gray-400">→</span>
                            <input type="date" value={customFim} onChange={e => setCustomFim(e.target.value)}
                                className="text-xs px-2 py-1.5 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 focus:outline-none" />
                        </span>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-16"><LoadingSpinner label="Carregando capturas..." size="sm" /></div>
            ) : (
                <>
                    {/* Dashboard de medidores */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {[
                            { label: 'Medidores Capturados', valor: stats.total, cls: 'text-gray-900 dark:text-white', Icon: Camera },
                            { label: 'Confirmados', valor: stats.confirmados, cls: 'text-emerald-600 dark:text-emerald-400', Icon: CheckCircle2 },
                            { label: 'Reconhecidos p/ OCR', valor: stats.reconhecidosOcr, cls: 'text-blue-600 dark:text-blue-400', Icon: ScanLine },
                            { label: 'Taxa de Sucesso OCR', valor: stats.taxaOcr == null ? '—' : `${stats.taxaOcr.toFixed(0)}%`, cls: 'text-blue-600 dark:text-blue-400', Icon: ScanLine },
                            { label: 'Aguardando Conferência', valor: stats.aguardando, cls: 'text-amber-600 dark:text-amber-400', Icon: Clock },
                            { label: 'Exportados', valor: stats.exportados, cls: 'text-purple-600 dark:text-purple-400', Icon: Send },
                            { label: 'Operadores Ativos', valor: stats.operadores, cls: 'text-gray-700 dark:text-gray-200', Icon: Users },
                            { label: 'Cidades Cobertas', valor: stats.cidades, cls: 'text-gray-700 dark:text-gray-200', Icon: Building2 },
                        ].map(({ label, valor, cls, Icon }) => (
                            <div key={label} className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
                                <div className="flex items-start justify-between">
                                    <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</p>
                                    <Icon size={18} className="text-gray-300 dark:text-gray-600 shrink-0" />
                                </div>
                                <p className={`text-xl font-bold mt-1 ${cls}`}>{valor}</p>
                            </div>
                        ))}
                    </div>

                    {/* Exportações — só saem capturas com nº confirmado (respeitando os filtros) */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-3 flex flex-wrap items-center gap-2">
                        <Download size={15} className="text-gray-400 ml-1" />
                        <span className="text-xs font-bold text-gray-700 dark:text-gray-200">
                            Exportar medidores <span className="font-normal text-gray-400">({exportRows.length} c/ nº confirmado)</span>
                        </span>
                        <div className="flex items-center gap-1.5 flex-wrap ml-auto">
                            <button onClick={() => exportar('excel')} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                                <FileSpreadsheet size={13} /> Excel
                            </button>
                            <button onClick={() => exportar('csv')} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                                <FileText size={13} /> CSV
                            </button>
                            <button onClick={() => exportar('pdf')} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                                <FileText size={13} /> PDF
                            </button>
                            <button onClick={iniciarExportEquatorial} className="flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-bold rounded-full bg-emerald-500 text-white hover:bg-emerald-600 transition-colors shadow-sm">
                                <Send size={13} /> Exportar Medidores para Equatorial
                            </button>
                        </div>
                    </div>

                    {/* Distribuições */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                            <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Capturas por Dia</h2>
                            {stats.porDia.length === 0 ? <p className="text-xs text-gray-400">Sem capturas.</p> : (
                                <div className="space-y-1.5">
                                    {stats.porDia.map(([dia, qtd]) => (
                                        <div key={dia} className="flex items-center gap-2">
                                            <span className="text-[11px] text-gray-500 dark:text-gray-400 w-12 shrink-0">{new Date(dia + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}</span>
                                            <div className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                                                <div className="h-full bg-blue-400 rounded-full" style={{ width: `${(qtd / maxDia) * 100}%` }}></div>
                                            </div>
                                            <span className="text-[11px] font-bold text-gray-700 dark:text-gray-200 w-6 text-right">{qtd}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                            <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Capturas por Operador</h2>
                            {stats.porOperador.length === 0 ? <p className="text-xs text-gray-400">Sem capturas.</p> : (
                                <div className="space-y-2">
                                    {stats.porOperador.slice(0, 8).map(([op, qtd], i) => (
                                        <div key={op} className="flex items-center gap-2.5">
                                            <span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center text-white shrink-0 ${i === 0 ? 'bg-amber-400' : 'bg-gray-300 dark:bg-gray-600'}`}>{i + 1}</span>
                                            <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 truncate flex-1">{nome(op)}</p>
                                            <span className="text-xs font-bold text-blue-600 dark:text-blue-400">{qtd}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                            <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Capturas por Cidade</h2>
                            {stats.porCidade.length === 0 ? <p className="text-xs text-gray-400">Sem capturas.</p> : (
                                <div className="space-y-2">
                                    {stats.porCidade.slice(0, 8).map(([cidade, qtd]) => {
                                        const pct = stats.total > 0 ? (qtd / stats.total) * 100 : 0;
                                        return (
                                            <div key={cidade}>
                                                <div className="flex justify-between text-xs mb-0.5">
                                                    <span className="text-gray-600 dark:text-gray-300">{cidade}</span>
                                                    <span className="font-bold text-gray-800 dark:text-gray-100">{qtd}</span>
                                                </div>
                                                <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                                                    <div className="h-full rounded-full bg-emerald-400" style={{ width: `${pct}%` }}></div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Galeria */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                        <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-1">Galeria de Capturas ({filtradas.length})</h2>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Clique na foto para abrir em tamanho real</p>
                        {filtradas.length === 0 ? (
                            <div className="text-center py-10 text-gray-400">
                                <Camera size={36} className="mx-auto mb-3 opacity-30" />
                                <p className="text-sm">Nenhuma captura no período/filtros selecionados.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                                {filtradas.map(c => (
                                    <div key={c.id} className="rounded-xl overflow-hidden border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
                                        <a href={c.foto_url} target="_blank" rel="noopener noreferrer" className="block relative group">
                                            <img src={c.foto_url} alt="Foto do medidor" loading="lazy" className="w-full h-32 object-cover group-hover:opacity-90 transition-opacity" />
                                            <span className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity"><ExternalLink size={11} /></span>
                                        </a>
                                        <div className="p-2.5">
                                            <p className="text-xs font-bold text-gray-800 dark:text-gray-100 truncate">{c.negociacoes?.titulo || 'Lead'}</p>
                                            <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                                                {[c.negociacoes?.bairro, c.negociacoes?.cidade].filter(Boolean).join(', ') || '—'}
                                            </p>
                                            {c.numero_medidor ? (
                                                <p className="text-[10px] font-mono font-bold text-blue-600 dark:text-blue-400 truncate mt-0.5">
                                                    🔢 {c.numero_medidor}{c.medidor_exportado_em ? ' · exportado ✓' : ''}
                                                </p>
                                            ) : (
                                                <p className="text-[10px] font-semibold text-amber-500 dark:text-amber-400 truncate mt-0.5">
                                                    {statusLeitura(c) === 'aguardando' ? '⏳ Aguardando conferência' : statusLeitura(c) === 'ocr_falhou' ? '⚠️ OCR falhou' : '— Sem número'}
                                                </p>
                                            )}
                                            <div className="flex items-center justify-between mt-1 gap-1">
                                                <span className="text-[10px] text-gray-400 flex items-center gap-0.5 truncate">
                                                    <Clock size={9} />
                                                    {c.checkin_em ? `${new Date(c.checkin_em).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${new Date(c.checkin_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : '—'}
                                                </span>
                                                {c.checkin_lat && c.checkin_lng && (
                                                    <a
                                                        href={`https://www.google.com/maps/search/?api=1&query=${c.checkin_lat},${c.checkin_lng}`}
                                                        target="_blank" rel="noopener noreferrer"
                                                        className="text-[10px] font-bold text-primary flex items-center gap-0.5 shrink-0"
                                                        title="Local exato da captura"
                                                    >
                                                        <MapPin size={9} /> local
                                                    </a>
                                                )}
                                            </div>
                                            <p className="text-[10px] text-gray-400 truncate mt-0.5">por {nome(c.rotas_comerciais?.vendedor_id)}{c.rotas_comerciais?.nome ? ` · ${c.rotas_comerciais.nome}` : ''}</p>
                                            {c.descricao_visita && <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate mt-0.5" title={c.descricao_visita}>📝 {c.descricao_visita}</p>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Alerta de medidores duplicados antes da exportação Equatorial */}
                    {dupModal && createPortal(
                        <div className="fixed inset-0 z-[2000] flex items-end sm:items-center justify-center sm:p-4 bg-black/50" onClick={() => setDupModal(null)}>
                            <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg flex flex-col overflow-hidden max-h-[92vh]" onClick={e => e.stopPropagation()}>
                                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                                    <div className="flex items-center gap-2.5">
                                        <div className="p-2 rounded-xl bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
                                            <AlertTriangle size={18} />
                                        </div>
                                        <div>
                                            <h2 className="text-sm font-bold text-gray-900 dark:text-white">Medidores duplicados</h2>
                                            <p className="text-[11px] text-gray-500 dark:text-gray-400">{dupModal.grupos.length} número(s) aparece(m) em mais de uma captura</p>
                                        </div>
                                    </div>
                                    <button onClick={() => setDupModal(null)} className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"><X size={18} /></button>
                                </div>
                                <div className="p-5 space-y-4 overflow-y-auto">
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Desmarque as capturas repetidas que não devem ir na planilha. A mais recente de cada número já vem selecionada.
                                    </p>
                                    {dupModal.grupos.map(([numero, grupo]) => (
                                        <div key={numero} className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50/40 dark:bg-amber-900/10 p-3 space-y-1.5">
                                            <p className="text-xs font-mono font-bold text-amber-700 dark:text-amber-300">Nº {numero} · {grupo.length} capturas</p>
                                            {grupo.map(r => (
                                                <label key={r.paradaId} className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-200 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={dupSelecionados.has(r.paradaId)}
                                                        onChange={e => setDupSelecionados(prev => {
                                                            const next = new Set(prev);
                                                            if (e.target.checked) next.add(r.paradaId); else next.delete(r.paradaId);
                                                            return next;
                                                        })}
                                                        className="rounded border-gray-300 text-primary focus:ring-primary/40"
                                                    />
                                                    <span className="truncate"><b>{r.empresa}</b> · {r.dataCaptura} · {r.operador}</span>
                                                </label>
                                            ))}
                                        </div>
                                    ))}
                                    <button
                                        onClick={() => concluirExportEquatorial(dupModal.rows.filter(r => dupSelecionados.has(r.paradaId)))}
                                        disabled={exportando || dupModal.rows.filter(r => dupSelecionados.has(r.paradaId)).length === 0}
                                        className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold rounded-xl bg-emerald-500 text-white hover:bg-emerald-600 transition-colors disabled:opacity-40"
                                    >
                                        <Send size={15} />
                                        {exportando ? 'Gerando...' : `Exportar ${dupModal.rows.filter(r => dupSelecionados.has(r.paradaId)).length} medidor(es)`}
                                    </button>
                                </div>
                            </div>
                        </div>,
                        document.body
                    )}
                </>
            )}
        </div>
    );
};

export default CapturasPanel;
